import React, { useState, useEffect } from 'react';
import { useFirebase } from '../contexts/FirebaseContext'; // Import useFirebase instead of useAuth0
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import '../styles/Settings.css'; // Import Settings CSS

const Settings = ({ onLanguageChange, onSpanishRegionChange }) => {
    const { isAuthenticated } = useFirebase(); // Use Firebase hooks
    const [baseLanguage, setBaseLanguage] = useState('English'); // New state for Base/Native Language
    const [learningLanguage, setLearningLanguage] = useState('Spanish');
    const [languageLevel, setLanguageLevel] = useState('Beginner');
    const [daysOfReview, setDaysOfReview] = useState(4);
    const [repeatPercentage, setRepeatPercentage] = useState(0);
    const [dailyStoryRepeatNumber, setDailyStoryRepeatNumber] = useState(0);
    const [spanishRegion, setSpanishRegion] = useState('Latin America');
    const [memoryEnabled, setMemoryEnabled] = useState(false); // New state for Bojee Memory toggle
    const [japaneseSpeechStyle, setJapaneseSpeechStyle] = useState('neutral'); // New state for Japanese speech style
    const [chineseCharacterType, setChineseCharacterType] = useState('Simplified'); // New state for Chinese character type
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    // Helper to generate numeric options.
    const generateOptions = (min, max, step) => {
        const options = [];
        for (let i = min; i <= max; i += step) {
            options.push(i);
        }
        return options;
    };

    // Fetch settings from the backend on mount.
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // Force a fresh fetch of settings when the Settings page is opened
                // This ensures the settings page always shows the most up-to-date values
                logger.debug('Settings - Fetching fresh settings on component mount');

                // Skip cache even if we've recently fetched settings
                window.FORCE_SETTINGS_REFRESH = true;

                // Prevent duplicate calls while a fetch is in progress
                if (window.settingsFetchInProgress) {
                    logger.debug('Settings - Settings fetch already in progress, waiting...');
                    // Wait for the in-progress fetch to complete
                    let attempts = 0;
                    const checkProgress = () => {
                        setTimeout(() => {
                            if (!window.settingsFetchInProgress) {
                                // Fetch has completed, update from localStorage
                                logger.debug('Settings - Using recently fetched settings from localStorage');
                                setBaseLanguage(localStorage.getItem('baseLanguage') || 'English');
                                setLearningLanguage(localStorage.getItem('learningLanguage') || 'Spanish');
                                setLanguageLevel(localStorage.getItem('languageLevel') || 'Beginner');
                                setDaysOfReview(Number(localStorage.getItem('daysOfReview')) || 4);
                                setRepeatPercentage(Number(localStorage.getItem('repeatPercentage')) || 0);
                                setDailyStoryRepeatNumber(Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0);
                                setMemoryEnabled(localStorage.getItem('memoryEnabled') === 'true');
                                setJapaneseSpeechStyle(localStorage.getItem('japaneseSpeechStyle') || 'neutral');
                                setChineseCharacterType(localStorage.getItem('chineseCharacterType') || 'Simplified');
                                setSpanishRegion(localStorage.getItem('spanishRegion') || 'Latin America');
                            } else if (attempts < 10) {
                                // Still waiting, check again
                                attempts++;
                                checkProgress();
                            } else {
                                // Too many attempts, proceed with direct fetch
                                logger.debug('Settings - Fetch still in progress after waiting, proceeding with direct fetch');
                                proceedWithFetch();
                            }
                        }, 200);
                    };
                    checkProgress();
                    return;
                }

                // Proceed with a direct fetch
                const proceedWithFetch = async () => {
                    window.settingsFetchInProgress = true;

                    // Use apiClient that handles token refresh automatically
                    logger.debug('Settings - Fetching settings from API...');
                    const response = await apiClient.get('/api/settings');

                    // Update the last fetch time and clear the in-progress flag
                    window.lastSettingsFetchTime = Date.now();
                    window.settingsFetchInProgress = false;

                    if (!response.ok) {
                        throw new Error(`Failed to fetch settings: ${response.status}`);
                    }

                    const data = await response.json();
                    logger.debug('Settings - Fetched successfully:', data);
                    logger.debug('Settings - Fetched memory state from server:', data.memoryEnabled);

                    setBaseLanguage(data.baseLanguage || 'English');
                    setLearningLanguage(data.learningLanguage || 'Spanish');
                    setLanguageLevel(data.languageLevel || 'Beginner');
                    setDaysOfReview(data.daysOfReview || 4);
                    setRepeatPercentage(data.repeatPercentage || 0);
                    setDailyStoryRepeatNumber(data.dailyStoryRepeatNumber || 0);
                    setMemoryEnabled(data.memoryEnabled || false); // Load memory setting
                    setJapaneseSpeechStyle(data.japaneseSpeechStyle || 'neutral'); // Load Japanese speech style
                    setChineseCharacterType(data.chineseCharacterType || 'Simplified'); // Load Chinese character type
                    if (data.learningLanguage === 'Spanish') {
                        setSpanishRegion(data.spanishRegion || 'Latin America');
                    } else {
                        setSpanishRegion('');
                    }

                    // Also save settings to localStorage
                    localStorage.setItem('baseLanguage', data.baseLanguage || 'English');
                    localStorage.setItem('learningLanguage', data.learningLanguage || 'Spanish');
                    localStorage.setItem('languageLevel', data.languageLevel || 'Beginner');
                    localStorage.setItem('daysOfReview', data.daysOfReview || 4);
                    localStorage.setItem('repeatPercentage', data.repeatPercentage || 0);
                    localStorage.setItem('dailyStoryRepeatNumber', data.dailyStoryRepeatNumber || 0);
                    localStorage.setItem('memoryEnabled', Boolean(data.memoryEnabled).toString());
                    localStorage.setItem('japaneseSpeechStyle', data.japaneseSpeechStyle || 'neutral');
                    localStorage.setItem('chineseCharacterType', data.chineseCharacterType || 'Simplified');
                    logger.debug('Settings - Saved memory state to localStorage:', data.memoryEnabled || false, 'as string:', Boolean(data.memoryEnabled).toString());

                    if (data.learningLanguage === 'Spanish') {
                        localStorage.setItem('spanishRegion', data.spanishRegion || 'Latin America');
                    }
                };

                await proceedWithFetch();
            } catch (err) {
                // Clear the in-progress flag in case of error
                window.settingsFetchInProgress = false;

                logger.error('Error fetching settings:', err);

                // Clear error after 5 seconds
                setError(`Error loading settings: ${err.message}`);
                setTimeout(() => setError(null), 5000);

                // Fall back to localStorage if available
                const storedLanguage = localStorage.getItem('learningLanguage');
                if (storedLanguage) {
                    logger.debug('Using settings from localStorage as fallback');
                    setBaseLanguage(localStorage.getItem('baseLanguage') || 'English');
                    setLearningLanguage(localStorage.getItem('learningLanguage') || 'Spanish');
                    setLanguageLevel(localStorage.getItem('languageLevel') || 'Beginner');
                    setDaysOfReview(Number(localStorage.getItem('daysOfReview')) || 3);
                    setRepeatPercentage(Number(localStorage.getItem('repeatPercentage')) || 0);
                    setDailyStoryRepeatNumber(Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0);
                    const memoryState = localStorage.getItem('memoryEnabled') === 'true';
                    setMemoryEnabled(memoryState); // Load from localStorage
                    setJapaneseSpeechStyle(localStorage.getItem('japaneseSpeechStyle') || 'neutral');
                    setChineseCharacterType(localStorage.getItem('chineseCharacterType') || 'Simplified');
                    logger.debug('Settings - Loaded memory state from localStorage fallback:', memoryState);
                    setSpanishRegion(localStorage.getItem('spanishRegion') || 'Latin America');
                }
            }
        };

        // Only fetch if authenticated or has token
        if (isAuthenticated || localStorage.getItem('access_token')) {
            fetchSettings();
        }
    }, [isAuthenticated]);

    const handleSave = async () => {
        try {
            logger.debug('Saving settings:', { baseLanguage, learningLanguage, languageLevel });
            logger.debug('Settings - Saving with memoryEnabled:', memoryEnabled);

            // apiClient.put() returns parsed JSON on success, or throws on HTTP error
            // (fetchWithAuth throws for non-OK status codes)
            const result = await apiClient.put('/api/settings', {
                baseLanguage,
                learningLanguage,
                languageLevel,
                daysOfReview,
                repeatPercentage,
                dailyStoryRepeatNumber,
                spanishRegion: learningLanguage === 'Spanish' ? spanishRegion : '',
                memoryEnabled, // Add memory enabled setting
                japaneseSpeechStyle: learningLanguage === 'Japanese' ? japaneseSpeechStyle : 'neutral', // Add Japanese speech style
                chineseCharacterType: learningLanguage === 'Chinese' ? chineseCharacterType : 'Simplified',
            });

            // Create a settings object using the current local state as a backup
            // This ensures we have valid data even if server response parsing fails
            const settingsData = {
                baseLanguage,
                learningLanguage,
                languageLevel,
                daysOfReview,
                repeatPercentage,
                dailyStoryRepeatNumber,
                spanishRegion: learningLanguage === 'Spanish' ? spanishRegion : '',
                memoryEnabled,
                japaneseSpeechStyle: learningLanguage === 'Japanese' ? japaneseSpeechStyle : 'neutral',
                chineseCharacterType: learningLanguage === 'Chinese' ? chineseCharacterType : 'Simplified',
            };

            // Merge server response data if available (result could be parsed JSON or a Response object)
            if (result && typeof result === 'object') {
                if (result.learningLanguage) {
                    // result is parsed JSON from the server
                    Object.assign(settingsData, result);
                } else if (typeof result.json === 'function') {
                    // result is a raw Response object (edge case), try to parse it
                    try {
                        const responseData = await result.json();
                        if (responseData && typeof responseData === 'object') {
                            Object.assign(settingsData, responseData);
                        }
                    } catch (innerJsonError) {
                        logger.error('Settings - Failed to parse response as JSON:', innerJsonError);
                    }
                }
            }

            logger.debug('Settings saved successfully:', settingsData);
            logger.debug('Settings - Memory state saved:', settingsData.memoryEnabled);

            // Call the language change callback from App.js
            if (onLanguageChange) {
                onLanguageChange(settingsData.learningLanguage);
            }

            // Call the Spanish region change callback from App.js
            if (learningLanguage === 'Spanish' && onSpanishRegionChange) {
                onSpanishRegionChange(settingsData.spanishRegion);
            }

            // Store settings in localStorage for other components to access
            localStorage.setItem('baseLanguage', settingsData.baseLanguage);
            localStorage.setItem('learningLanguage', settingsData.learningLanguage);
            localStorage.setItem('languageLevel', settingsData.languageLevel);
            localStorage.setItem('daysOfReview', settingsData.daysOfReview);
            localStorage.setItem('repeatPercentage', settingsData.repeatPercentage);
            localStorage.setItem('dailyStoryRepeatNumber', settingsData.dailyStoryRepeatNumber);
            localStorage.setItem('memoryEnabled', Boolean(settingsData.memoryEnabled).toString());
            localStorage.setItem('japaneseSpeechStyle', settingsData.japaneseSpeechStyle);
            localStorage.setItem('chineseCharacterType', settingsData.chineseCharacterType);
            logger.debug('Settings - Memory state saved to localStorage:', settingsData.memoryEnabled, 'as string:', Boolean(settingsData.memoryEnabled).toString());

            if (settingsData.learningLanguage === 'Spanish') {
                localStorage.setItem('spanishRegion', settingsData.spanishRegion);
            }

            // Mark that we want a full refresh of settings on next check
            // This helps synchronize settings across tabs and browsers
            window.FORCE_SETTINGS_REFRESH = true;
            window.lastSettingsFetchTime = Date.now();

            // Broadcast the settings change to other tabs/windows using the same origin
            try {
                localStorage.setItem('lastSettingsUpdate', Date.now().toString());
                const updateEvent = new CustomEvent('settings-updated', {
                    detail: {
                        timestamp: Date.now(),
                        settings: settingsData
                    }
                });
                window.dispatchEvent(updateEvent);
            } catch (e) {
                logger.error('Settings - Error broadcasting settings update:', e);
            }

            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
            setError(null);
        } catch (err) {
            logger.error('Error saving settings:', err);
            setError(`Error saving settings: ${err.message}`);
            setTimeout(() => setError(null), 5000);
        }
    };

    // Add a handler for Spanish region changes
    const handleSpanishRegionChange = (region) => {
        setSpanishRegion(region);
        localStorage.setItem('spanishRegion', region);

        // Notify parent component
        if (onSpanishRegionChange) {
            onSpanishRegionChange(region);
        }
    };

    // Add a handler for Chinese character type changes
    const handleChineseCharacterTypeChange = (type) => {
        setChineseCharacterType(type);
        localStorage.setItem('chineseCharacterType', type);
    };

    // Toggle handler for memory switch
    const handleMemoryToggle = () => {
        const newState = !memoryEnabled;
        logger.debug(`Settings - Memory toggle switched from ${memoryEnabled ? 'ON' : 'OFF'} to ${newState ? 'ON' : 'OFF'}`);
        setMemoryEnabled(newState);
    };

    return (
        <>
            <div className="page-title">Settings</div>
            <div className="settings-container">
                <div className="settings-menu-container">

                    {/* NEW SECTION: Native / Base Language */}
                    <section className="settings-section">
                        <div className="settings-group">
                            <span className="global-menu-title settings-menu-title">Your Native Language:</span>
                            <select
                                id="baseLanguage"
                                name="baseLanguage"
                                value={baseLanguage}
                                onChange={(e) => setBaseLanguage(e.target.value)}
                                className="settings-select"
                            >
                                <option value="English">English</option>
                                <option value="Spanish">Spanish</option>
                                <option value="French">French</option>
                                <option value="German">German</option>
                                <option value="Italian">Italian</option>
                                <option value="Portuguese">Portuguese</option>
                                <option value="Dutch">Dutch</option>
                                <option value="Russian">Russian</option>
                                <option value="Chinese">Chinese</option>
                                <option value="Japanese">Japanese</option>
                                <option value="Korean">Korean</option>
                                {/* Add or remove languages from this list as needed */}
                            </select>
                            <p className="settings-tagline">Translations and definitions will appear in this language.</p>
                        </div>
                    </section>

                    <section className="settings-section">
                        <div className="settings-group">
                            <span className="global-menu-title settings-menu-title">Select your target language:</span>
                            <select
                                id="learningLanguage"
                                name="learningLanguage"
                                value={learningLanguage}
                                onChange={(e) => {
                                    setLearningLanguage(e.target.value);
                                    if (e.target.value !== 'Spanish') {
                                        setSpanishRegion('');
                                    }
                                }}
                                className="settings-select"
                            >
                                <option value="Chinese">Chinese</option>
                                <option value="French">French</option>
                                <option value="Italian">Italian</option>
                                <option value="Japanese">Japanese</option>
                                <option value="Korean">Korean</option>
                                <option value="Spanish">Spanish</option>
                            </select>
                            <p className="settings-tagline">Choose the language you want to learn.</p>
                        </div>
                    </section>

                    {learningLanguage === 'Spanish' && (
                        <section className="settings-section">
                            <div className="settings-group">
                                <span className="global-menu-title settings-menu-title">Select region:</span>
                                <select
                                    value={spanishRegion}
                                    onChange={(e) => handleSpanishRegionChange(e.target.value)}
                                    className="settings-select"
                                >
                                    <option value="Latin America">Latin America</option>
                                    <option value="Spain">Spain</option>
                                </select>
                                <p className="settings-tagline">Choose the region of Spanish you'd like to study.</p>
                            </div>
                        </section>
                    )}
                    {learningLanguage === 'Chinese' && (
                        <section className="settings-section">
                            <div className="settings-group">
                                <span className="global-menu-title settings-menu-title">Chinese Character Type:</span>
                                <select
                                    value={chineseCharacterType}
                                    onChange={(e) => handleChineseCharacterTypeChange(e.target.value)}
                                    className="settings-select"
                                >
                                    <option value="Simplified">Simplified</option>
                                    <option value="Traditional">Traditional</option>
                                </select>
                                <p className="settings-tagline">Choose which type of Chinese characters you want to learn.</p>
                            </div>
                        </section>
                    )}
                    <section className="settings-section">
                        <div className="settings-group">
                            <span className="global-menu-title settings-menu-title">Language Level:</span>
                            <select
                                value={languageLevel}
                                onChange={(e) => setLanguageLevel(e.target.value)}
                                className="settings-select"
                            >
                                <option value="Beginner">Beginner</option>
                                <option value="Intermediate">Intermediate</option>
                                <option value="Advanced">Advanced</option>
                            </select>
                            <p className="settings-tagline">
                                Choose your language proficiency level. This will affect the difficulty of the sentences and stories.
                            </p>
                        </div>
                    </section>
                    <section className="settings-section">
                        <div className="settings-group">
                            <span className="global-menu-title settings-menu-title">Days of Review:</span>
                            <select
                                value={daysOfReview}
                                onChange={(e) => setDaysOfReview(Number(e.target.value))}
                                className="settings-select"
                            >
                                {generateOptions(1, 10, 1).map((num) => (
                                    <option key={num} value={num}>
                                        {num}
                                    </option>
                                ))}
                            </select>
                            <p className="settings-tagline">
                                Choose the number of days to review a playlist before it's erased. Recommended: 3 to 4.
                            </p>
                        </div>
                    </section>
                    <section className="settings-section">
                        <div className="settings-group">
                            <span className="global-menu-title settings-menu-title">
                                % of Sentences with previously saved words:
                            </span>
                            <select
                                value={repeatPercentage}
                                onChange={(e) => setRepeatPercentage(Number(e.target.value))}
                                className="settings-select"
                            >
                                {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((num) => (
                                    <option key={num} value={num}>
                                        {num}%
                                    </option>
                                ))}
                            </select>
                            <p className="settings-tagline">
                                Choose the % of sentences that will contain your saved word(s).
                            </p>
                        </div>
                    </section>
                    <section className="settings-section">
                        <div className="settings-group">
                            <span className="global-menu-title settings-menu-title">
                                # of Previously Saved Words in Stories:
                            </span>
                            <select
                                value={dailyStoryRepeatNumber}
                                onChange={(e) => setDailyStoryRepeatNumber(Number(e.target.value))}
                                className="settings-select"
                            >
                                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                    <option key={num} value={num}>
                                        {num}
                                    </option>
                                ))}
                            </select>
                            <p className="settings-tagline">
                                Choose the number of your saved words to reappear in your daily stories. Recommended:
                                {`<`}5. Stories might become more non-sensical with higher values.
                            </p>
                        </div>
                    </section>
                    {learningLanguage === 'Japanese' && (
                        <section className="settings-section">
                            <div className="settings-group">
                                <span className="global-menu-title settings-menu-title">Japanese Fluent Mode:</span>
                                <select
                                    value={japaneseSpeechStyle}
                                    onChange={(e) => setJapaneseSpeechStyle(e.target.value)}
                                    className="settings-select"
                                >
                                    <option value="feminine">Feminine</option>
                                    <option value="masculine">Masculine</option>
                                    <option value="neutral">Neutral</option>
                                </select>
                                <p className="settings-tagline">
                                    Choose how Fluent Mode adjusts your speech tone with Bojee — Feminine, Masculine, or Neutral. Neutral keeps your tone as balanced as possible.
                                </p>
                            </div>
                        </section>
                    )}
                    <section className="settings-section">
                        <div className="settings-group">
                            <span className="global-menu-title settings-menu-title">Bojee Memory (Beta)</span>
                            <div className="settings-memory-toggle-container">
                                <div className={`settings-memory-toggle ${memoryEnabled ? 'on' : 'off'}`} onClick={handleMemoryToggle}>
                                    <div className="settings-memory-state-text">
                                        {memoryEnabled ? 'ON' : 'OFF'}
                                    </div>
                                    <div className="settings-memory-slider"></div>
                                </div>
                            </div>
                            <p className="settings-tagline">
                                Turn on if you would like Bojee to recall various things you mention about yourself in your conversations.
                            </p>
                        </div>
                    </section>
                    <button onClick={handleSave} className="global-save-button settings-save-button">
                        Save Settings
                    </button>
                    {error && <p style={{ color: 'red', textAlign: 'center', maxWidth: '600px', margin: '10px auto' }}>{error}</p>}
                    {saved && <p className="settings-saved-text">Settings saved!</p>}
                </div>
            </div>
        </>
    );
};

export default Settings;